import React, { useState } from 'react';
import { Spinner, useStateContext } from '@veda/dashboard-ui';
import { usePppApi } from '../services/pppApi';
import { useDownload } from '../hooks/useDownload';

function KpiCard({ label, value, sub, color }) {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700',
    red:    'bg-red-50 dark:bg-red-900/20 text-red-700',
    orange: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700',
    gray:   'bg-gray-50 dark:bg-gray-900/20 text-gray-700',
  };
  return (
    <div className={`rounded-xl p-4 text-center ${colors[color]}`}>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs mt-0.5 opacity-70">{sub}</div>}
      <div className="text-xs mt-1 opacity-80">{label}</div>
    </div>
  );
}

const today = new Date().toISOString().slice(0, 10);
const firstOfYear = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);

export default function ReportStatistiche() {
  const { _locate } = useStateContext();
  const api = usePppApi();
  const { download, downloading } = useDownload();
  const [filters, setFilters] = useState({ periodo_inizio: firstOfYear, periodo_fine: today, stato: '' });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const carica = () => {
    setLoading(true);
    setReport(null);
    api.getStatisticheAggregate(filters, {
      onSuccess: (data) => { setReport(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  };

  const exportExcel = () => {
    const filename = `statistiche_ppp_${filters.periodo_inizio}_${filters.periodo_fine}.csv`;
    download(api.urlStatisticheExcel(filters), filename);
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          {_locate('ppp_report_statistiche_titolo')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {_locate('ppp_report_statistiche_desc')}
        </p>
      </div>

      {/* Filtri */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{_locate('ppp_report_periodo_inizio')}</label>
          <input
            type="date"
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            value={filters.periodo_inizio}
            max={filters.periodo_fine}
            onChange={(e) => setFilters((f) => ({ ...f, periodo_inizio: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{_locate('ppp_report_periodo_fine')}</label>
          <input
            type="date"
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            value={filters.periodo_fine}
            min={filters.periodo_inizio}
            onChange={(e) => setFilters((f) => ({ ...f, periodo_fine: e.target.value }))}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">{_locate('ppp_report_stato')}</label>
          <select
            className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            value={filters.stato}
            onChange={(e) => setFilters((f) => ({ ...f, stato: e.target.value }))}
          >
            <option value="">{_locate('ppp_report_tutti_gli_stati')}</option>
            <option value="bozza">Bozza</option>
            <option value="avviato">Avviato</option>
            <option value="sospeso">Sospeso</option>
            <option value="concluso">Concluso</option>
            <option value="archiviato">Archiviato</option>
          </select>
        </div>
        <button
          onClick={carica}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
        >
          {loading ? _locate('ppp_report_caricamento') : _locate('ppp_report_genera')}
        </button>
        {report && (
          <button onClick={exportExcel} disabled={downloading} className="px-4 py-2 bg-green-700 text-white rounded-lg text-sm font-medium hover:bg-green-800 disabled:opacity-50 transition-colors">
            {downloading ? '...' : 'Excel (CSV)'}
          </button>
        )}
      </div>

      {loading && <div className="flex justify-center items-center h-40"><Spinner /></div>}

      {report && (
        <div className="space-y-6">
          {/* KPI principali */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            <KpiCard label={_locate('ppp_report_stats_totale')} value={report.totaleProcedimenti} color="blue" />
            <KpiCard label={_locate('ppp_report_stats_attivi')} value={report.procedimentiAttivi} color="blue" />
            <KpiCard label={_locate('ppp_report_stats_conclusi')} value={report.procedimentiConclusi} color="green" />
            <KpiCard label={_locate('ppp_report_stats_sospesi')} value={report.procedimentiSospesi} color="orange" />
            <KpiCard label={_locate('ppp_report_stats_conformita')} value={`${report.percentualeConformita}%`} color={report.percentualeConformita >= 80 ? 'green' : 'red'} />
            <KpiCard label={_locate('ppp_report_stats_ritardi')} value={report.scadenzeInRitardo} color="red" />
            <KpiCard label={_locate('ppp_report_stats_prorogate')} value={report.scadenzeProrogate} color="orange" />
          </div>

          {/* Statistiche per fase */}
          {report.statisticheFasi.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">{_locate('ppp_report_stats_per_fase')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_fase')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_stats_n_proc')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_stats_media_giorni')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_stats_conformita_fase')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {report.statisticheFasi.map((f) => (
                      <tr key={f.fase} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3 text-gray-500 text-xs">{f.fase}</td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{f.nomeFase}</td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{f.totaleProcedimenti}</td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">
                          {f.mediaGiorni !== null ? `${f.mediaGiorni} gg` : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-semibold ${f.percentualeRispettoTermini >= 80 ? 'text-green-600' : 'text-red-600'}`}>
                            {f.percentualeRispettoTermini}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Statistiche per RUP */}
          {report.statisticheRup.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="font-semibold text-gray-900 dark:text-white">{_locate('ppp_report_stats_per_rup')}</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">RUP</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_perf_assegnati')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_perf_conclusi_ok')}</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase">{_locate('ppp_report_perf_ritardi')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                    {report.statisticheRup.map((r) => (
                      <tr key={r.rupId} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{r.rupNome}</td>
                        <td className="px-4 py-3 text-center text-gray-600 dark:text-gray-300">{r.assegnati}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="text-green-600 font-semibold">{r.conclusiNeiTermini}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={r.ritardi > 0 ? 'text-red-600 font-semibold' : 'text-gray-600 dark:text-gray-300'}>
                            {r.ritardi}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
